/**
 * Dev-only branding selector + preview for the Auth page.
 *
 * Lets you switch between Wavlynq + 9 sibling apps to verify:
 *   - Logo + icon assets resolve
 *   - Theme tokens apply (themeClass)
 *   - Redirect URLs the edge functions will receive
 *
 * Hidden on production hosts (live custom domains). Visible on
 * preview/staging hosts and localhost.
 */
import { useMemo, useState } from "react";
import { APPS, WAVLYNQ_LOGO, type AppId } from "@/lib/apps";
import { getAppForHost } from "@/lib/hostname/appForHost";
import { Button } from "@/components/ui/button";

type PanelKey = "wavlynq" | AppId;

const PROD_HOSTS = new Set([
  "wavlynq.com", "www.wavlynq.com",
  "fryqnc.com", "www.fryqnc.com",
  "pulsynq.com", "www.pulsynq.com",
  "mindlynq.com", "www.mindlynq.com",
  "ablelynq.com", "www.ablelynq.com",
  "deafwav.com", "www.deafwav.com",
  "fullynq.com", "www.fullynq.com",
  "jadesynq.com", "www.jadesynq.com",
  "openlynq.com", "www.openlynq.com",
  "spiritsynq.com", "www.spiritsynq.com",
]);

interface Props {
  next: string;
}

const BrandPreviewPanel = ({ next }: Props) => {
  const detected = useMemo<PanelKey>(() => getAppForHost(), []);
  const [selected, setSelected] = useState<PanelKey>(detected);
  const [open, setOpen] = useState(false);

  // Hide on production custom domains
  if (typeof window !== "undefined" && PROD_HOSTS.has(window.location.hostname.toLowerCase())) {
    return null;
  }

  const isUmbrella = selected === "wavlynq";
  const app = isUmbrella ? null : APPS.find((a) => a.id === selected);
  const logo = app?.logo ?? WAVLYNQ_LOGO;
  const themeClass = app?.themeClass ?? "";
  const name = app?.name ?? "Wavlynq";
  const community = app?.community ?? "Umbrella portal";

  // All auth flows route through the Wavlynq umbrella regardless of which
  // sibling app the member entered from — single source of truth for OAuth
  // allowlists, magic-link callbacks, and password reset.
  const AUTH_ORIGIN = "https://www.wavlynq.com";
  const signupRedirect = `${AUTH_ORIGIN}${next}`;
  const magicRedirect = `${AUTH_ORIGIN}/auth/callback?next=${encodeURIComponent(next)}`;
  const resetRedirect = `${AUTH_ORIGIN}/reset-password`;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      {!open ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="shadow-lg backdrop-blur"
        >
          🎨 Brand preview · {name}
        </Button>
      ) : (
        <div className={`${themeClass} rounded-xl border border-border bg-card text-card-foreground shadow-2xl p-4 space-y-4`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Dev brand preview
              </p>
              <p className="text-sm font-semibold mt-0.5">{name}</p>
              <p className="text-xs text-muted-foreground">{community}</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-muted-foreground hover:text-foreground"
              aria-label="Close brand preview"
            >
              ✕
            </button>
          </div>

          <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
            <img src={logo} alt={`${name} logo`} className="h-8 w-auto" />
            <div className="flex-1 flex gap-1.5">
              <span className="h-6 w-6 rounded-md bg-primary" title="primary" />
              <span className="h-6 w-6 rounded-md bg-secondary" title="secondary" />
              <span className="h-6 w-6 rounded-md bg-accent" title="accent" />
              <span className="h-6 w-6 rounded-md bg-muted" title="muted" />
            </div>
          </div>

          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
              Switch app
            </p>
            <div className="grid grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setSelected("wavlynq")}
                className={`text-xs rounded-md px-2 py-1 border text-left ${
                  selected === "wavlynq"
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                Wavlynq
              </button>
              {APPS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelected(a.id)}
                  className={`text-xs rounded-md px-2 py-1 border text-left ${
                    selected === a.id
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {a.name}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Redirects edge functions will receive
            </p>
            <RedirectRow label="Signup confirm" value={signupRedirect} />
            <RedirectRow label="Magic link" value={magicRedirect} />
            <RedirectRow label="Password reset" value={resetRedirect} />
          </div>

          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Detected from host: <span className="text-foreground">{detected}</span>. Selecting another
            app here only previews assets &amp; tokens — actual routing is driven by hostname.
          </p>
        </div>
      )}
    </div>
  );
};

const RedirectRow = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md border border-border bg-muted/30 px-2 py-1.5">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-[11px] font-mono break-all text-foreground">{value}</p>
  </div>
);

export default BrandPreviewPanel;
